/**
 * Utilities for safely logging objects that may contain sensitive fields.
 *
 * Never log raw `password`, `confirmPassword`, `token`, `accessToken`,
 * `refreshToken`, etc. Use `redactSensitive(obj)` before passing form/auth
 * payloads to the logger.
 */

const DEFAULT_SENSITIVE_KEYS = [
    'password',
    'confirmPassword',
    'currentPassword',
    'newPassword',
    'token',
    'accessToken',
    'refreshToken',
    'apiKey',
    'secret',
];

/**
 * Returns a shallow copy of `obj` with sensitive keys replaced by '[REDACTED]'.
 * If the corresponding value is falsy (e.g. an empty form field), it is dropped
 * from the result instead of logging '[REDACTED]' for empty strings.
 *
 * @param {Record<string, unknown> | null | undefined} obj
 * @param {string[]} [extraKeys] - Extra keys to redact in addition to defaults
 * @returns {Record<string, unknown>}
 */
export const redactSensitive = (obj, extraKeys = []) => {
    if (!obj || typeof obj !== 'object') {
        return {};
    }

    const sensitive = new Set([...DEFAULT_SENSITIVE_KEYS, ...extraKeys]);
    const result = {};

    for (const [key, value] of Object.entries(obj)) {
        if (sensitive.has(key)) {
            if (value) {
                result[key] = '[REDACTED]';
            }
            continue;
        }
        result[key] = value;
    }

    return result;
};
